import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Workspace } from 'shared'
import { Alert, Button, Card, PageHeader } from '../../components/ui'
import WorkspaceProfileForm, {
  EMPTY_PROFILE,
  toApiPayload,
  type WorkspaceProfileValues,
} from '../../components/workspace/WorkspaceProfileForm'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

export default function WorkspaceNew() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [values, setValues] = useState<WorkspaceProfileValues>(EMPTY_PROFILE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The published initial template, if there is one: what a new company would
  // start with, offered so a blank workspace stays a choice.
  const [kickoffName, setKickoffName] = useState<string | null>(null)
  const [withKickoff, setWithKickoff] = useState(true)

  useEffect(() => {
    api
      .get<{ data: { name: string | null } }>('/api/workspaces/kickoff')
      .then((response) => setKickoffName(response.data.name))
      // Nothing to offer is not an error worth showing.
      .catch(() => setKickoffName(null))
  }, [])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!values.name.trim()) return

    setSaving(true)
    setError(null)

    try {
      const response = await api.post<{ data: Workspace; kickoff_failed?: boolean }>('/api/workspaces', {
        ...toApiPayload(values),
        skip_kickoff: kickoffName !== null && !withKickoff,
      })
      navigate(`/app/workspaces/${response.data.id}`, {
        state: response.kickoff_failed ? { kickoffFailed: true } : undefined,
      })
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
      setSaving(false)
    }
  }

  return (
    <>
      <PageHeader title={t('workspaces.createTitle')} subtitle={t('workspaces.createSubtitle')} />

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <WorkspaceProfileForm values={values} onChange={setValues} disabled={saving} />

          {kickoffName && (
            <label className="flex items-start gap-3 rounded-md border border-gray-200 p-4 text-sm dark:border-gray-800">
              <input
                type="checkbox"
                checked={withKickoff}
                onChange={(event) => setWithKickoff(event.target.checked)}
                disabled={saving}
                className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                <span className="block font-medium text-gray-900 dark:text-gray-50">
                  {t('workspaces.kickoffLabel', { name: kickoffName })}
                </span>
                <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                  {t('workspaces.kickoffHint')}
                </span>
              </span>
            </label>
          )}

          {error && <Alert>{error}</Alert>}

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-800">
            <Button type="button" variant="secondary" onClick={() => navigate('/app/workspaces')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!values.name.trim()}>
              {t('workspaces.create')}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}
